import React, { useEffect, useState } from "react";
import axios from "axios";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, FileDown, Link2 } from "lucide-react";
import { ReportView } from "@/components/ReportView";
import { LanguageToggle } from "@/components/LanguageToggle";
import { Button } from "@/components/ui/button";
import { useBranding } from "@/hooks/useBranding";
import { useI18n } from "@/i18n";
import { usePrintGuard } from "@/hooks/usePrintGuard";
import { downloadReportPdf } from "@/lib/reportPdf";
import { buildCover } from "@/lib/reportCover";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SharedReport() {
  const { token } = useParams();
  const { t, lang, setLang, locale } = useI18n();
  const { branding, logoUrl } = useBranding();
  const [report, setReport] = useState(null);
  const [status, setStatus] = useState("loading");
  const [pdfLoading, setPdfLoading] = useState(false);
  usePrintGuard();

  useEffect(() => {
    axios
      .get(`${API}/share/${token}`)
      .then(({ data }) => {
        setReport(data);
        setStatus("ok");
        if (data.lang && data.lang !== lang) setLang(data.lang);
      })
      .catch((e) => setStatus(e?.response?.status === 410 ? "expired" : "missing"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const brandName = branding.brand_name || "CoachMeUpp";

  const exportPdf = async () => {
    setPdfLoading(true);
    const id = toast.loading(t("app.preparingPdf"));
    try {
      await downloadReportPdf({
        companyName: report.company,
        brandName,
        cover: buildCover({
          t,
          locale,
          companyName: report.company,
          brandName,
          logoUrl,
          accent: branding.brand_color,
          bookingUrl: branding.booking_url,
          modelType: report.model_type,
          recommendations: report.recommendations,
        }),
      });
      toast.success(t("app.pdfReady"), { id });
    } catch (e) {
      console.error(e);
      toast.error(t("app.pdfFailed"), { id });
    } finally {
      setPdfLoading(false);
    }
  };

  if (status === "loading") {
    return (
      <div
        className="min-h-screen bg-[#DCE4EF] text-slate-600 flex items-center justify-center"
        data-testid="share-loading"
      >
        <Loader2 size={20} className="animate-spin mr-2" /> {t("share.loading")}
      </div>
    );
  }

  if (status !== "ok") {
    const expired = status === "expired";
    return (
      <div
        className="min-h-screen bg-[#DCE4EF] text-slate-900 flex items-center justify-center px-6"
        data-testid={expired ? "share-expired" : "share-missing"}
      >
        <div className="max-w-md text-center">
          <span className="inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-amber-500/10 mb-5">
            <Link2 size={24} className="text-amber-600" />
          </span>
          <h1 className="font-heading text-2xl font-extrabold mb-2">
            {t(expired ? "share.expiredTitle" : "share.notFoundTitle")}
          </h1>
          <p className="text-sm text-slate-600 mb-6">
            {t(expired ? "share.expiredDesc" : "share.notFoundDesc")}
          </p>
          <a href="/">
            <Button className="bg-indigo-600 hover:bg-indigo-500 text-white" data-testid="share-restart-btn">
              {t("share.startAgain")}
            </Button>
          </a>
        </div>
      </div>
    );
  }

  const validUntil = report.expires_at
    ? new Date(report.expires_at).toLocaleDateString(locale, { year: "numeric", month: "long", day: "numeric" })
    : "";

  return (
    <div className="min-h-screen bg-[#DCE4EF] text-slate-900" data-testid="share-page">
      <header className="no-print sticky top-0 z-40 backdrop-blur-md bg-white/85 border-b border-slate-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-3.5 flex items-center justify-between gap-3 flex-wrap">
          <div className="min-w-0">
            <p className="text-[11px] uppercase tracking-widest font-semibold text-amber-600">
              {t("share.badge")}
            </p>
            {validUntil && (
              <p className="text-xs text-slate-500 mt-0.5">{t("share.validUntil", { date: validUntil })}</p>
            )}
          </div>
          <div className="flex items-center gap-2">
            <LanguageToggle light />
            <Button
              onClick={exportPdf}
              disabled={pdfLoading}
              className="h-9 bg-indigo-600 hover:bg-indigo-500 text-white"
              data-testid="share-download-pdf-btn"
            >
              {pdfLoading ? (
                <Loader2 size={15} className="animate-spin sm:mr-1.5" />
              ) : (
                <FileDown size={15} className="sm:mr-1.5" />
              )}
              <span className="hidden sm:inline">{t("app.print")}</span>
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-8">
        <ReportView
          companyName={report.company}
          modelType={report.model_type}
          context={report.context}
          scores={report.scores}
          teamScores={report.team_scores}
          headcounts={report.headcounts}
          recommendations={report.recommendations}
          brandName={brandName}
          logoUrl={logoUrl}
          bookingUrl={branding.booking_url}
        />
      </main>
    </div>
  );
}
